import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { Search as SearchIcon } from "lucide-react";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { searchTemple, type SearchHit, type SearchKind } from "@/lib/search.functions";

const KIND_LABEL: Record<SearchKind, string> = {
  book: "Book",
  activation: "Activation",
  wisdom: "Wisdom",
  music: "Music",
  song: "Song",
  academy: "Academy",
};

const QUICK_LINKS: Array<{ label: string; to: string }> = [
  { label: "Home", to: "/" },
  { label: "Music", to: "/music" },
  { label: "Activations", to: "/activations" },
  { label: "Books", to: "/books" },
  { label: "Academy", to: "/academy" },
  { label: "Gates", to: "/gates" },
  { label: "Journey Map", to: "/gates/map" },
  { label: "Wisdom", to: "/wisdom" },
  { label: "Ask Buddha KA", to: "/ask" },
  { label: "Full search", to: "/search" },
];

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [hits, setHits] = useState<SearchHit[]>([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const runSearch = useServerFn(searchTemple);

  // Global Cmd/Ctrl+K
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.key === "k" || e.key === "K") && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    (window as unknown as { openCommandPalette?: () => void }).openCommandPalette = () => setOpen(true);
    return () => {
      window.removeEventListener("keydown", onKey);
      delete (window as unknown as { openCommandPalette?: () => void }).openCommandPalette;
    };
  }, []);

  // Debounced server search
  useEffect(() => {
    if (!open) return;
    const query = q.trim();
    if (query.length < 2) {
      setHits([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await runSearch({ data: { q: query, limit: 30 } });
        setHits(res.hits ?? []);
      } catch {
        setHits([]);
      } finally {
        setLoading(false);
      }
    }, 180);
    return () => clearTimeout(t);
  }, [q, open, runSearch]);

  const grouped = useMemo(() => {
    const g: Partial<Record<SearchKind, SearchHit[]>> = {};
    for (const h of hits) {
      const k = h.kind as SearchKind;
      (g[k] ??= []).push(h);
    }
    return g;
  }, [hits]);

  function go(to: string) {
    setOpen(false);
    setQ("");
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    navigate({ to: to as any });
  }

  function hitTo(h: SearchHit): string {
    switch (h.kind) {
      case "book": return `/books/${h.slug}`;
      case "activation": return `/activations/${h.slug}`;
      case "wisdom": return `/wisdom/${h.slug}`;
      case "music": return `/music/${h.slug}`;
      case "song": return `/music/${h.parent_slug}/song/${h.slug}`;
      case "academy": return `/academy/${h.parent_slug}/lesson/${h.slug}`;
      default: return "/search";
    }
  }

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput
        value={q}
        onValueChange={setQ}
        placeholder="Search music, activations, wisdom, books, lessons…"
      />
      <CommandList>
        {q.trim().length < 2 ? (
          <>
            <CommandGroup heading="Jump to">
              {QUICK_LINKS.map((l) => (
                <CommandItem key={l.to} value={l.label} onSelect={() => go(l.to)}>
                  <SearchIcon className="mr-2 h-3.5 w-3.5 text-gold" />
                  {l.label}
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
            <div className="px-3 py-2 text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
              Tip · press ⌘K anywhere · ↵ to open
            </div>
          </>
        ) : (
          <>
            {loading && hits.length === 0 && (
              <div className="px-3 py-6 text-center text-sm text-muted-foreground">Searching…</div>
            )}
            {!loading && hits.length === 0 && (
              <CommandEmpty>No results. Try another word.</CommandEmpty>
            )}
            {(Object.keys(grouped) as SearchKind[]).map((kind) => (
              <CommandGroup key={kind} heading={KIND_LABEL[kind]}>
                {grouped[kind]!.map((h) => (
                  <CommandItem
                    key={`${h.kind}:${h.parent_slug ?? ""}:${h.slug}`}
                    value={`${h.title} ${h.subtitle ?? ""} ${h.kind}`}
                    onSelect={() => go(hitTo(h))}
                  >
                    <div className="flex min-w-0 flex-col">
                      <span className="truncate">{h.title}</span>
                      {h.subtitle && (
                        <span className="truncate text-[11px] text-muted-foreground">{h.subtitle}</span>
                      )}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
            <CommandSeparator />
            <CommandItem value="__seeall" onSelect={() => go(`/search?q=${encodeURIComponent(q.trim())}`)}>
              <SearchIcon className="mr-2 h-3.5 w-3.5 text-gold" />
              See all results for “{q.trim()}”
            </CommandItem>
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
}
